// DS: TA0.2M
import React, { useCallback, useRef } from 'react';
import classnames from 'classnames';
import UserAvatar from '@wix/da-shared-react/pkg/User/Avatar';
import UserLink from '@wix/da-shared-react/pkg/User/Link';
import { useFlag } from '@wix/da-react-context/pkg/flags/hooks';
import { ProfileOwnerSubscribeButton } from '@wix/da-shared-react/pkg/Button';
import { getIsGroupProfile } from '../../../selectors/users';
import { useSelector } from 'react-redux';
import AvatarBirthdayBadge from '../ProfileIdentity/AvatarBirthdayBadge';
import JoinGroupButton from '@wix/da-shared-react/pkg/JoinGroupButton';
import MoreActionsDropdownMobile from '../ProfileIdentity/MoreActionsDropdown/Mobile';
import MonetizationPromoBanner from '../MonetizationPromoBanner';
import WatchButton from './WatchButton';
import NotesButton from './NotesButton';
import SkinButton from './SkinButton';
import { PapiDeviation, PapiUser } from '@wix/da-papi-types';
import ProfileStatItem from '../ProfileIdentity/ProfileStatItem';
import AvatarEdit from '../ProfileIdentity/AvatarEdit';
import { useHeroImageForContainer } from '@wix/da-shared-react/pkg/utils/hooks/useHeroImageForContainer';
import { setIsEditorOpen } from '../../../actions/avatar';

import s from './ProfileHeroMobile.scss';

export interface Props {
  className?: string;
  coverDeviation?: PapiDeviation;
  profileOwnerUser: PapiUser;
  canChangeAvatar: boolean;
  isAuthorizedToCustomize: boolean;
  isOwner: boolean;
  hasSubscriptions: boolean;
  isGroup: boolean;
  stats: object;
  tagline?: string;
  openAvatarEditor: typeof setIsEditorOpen;
}

export const ProfileHeroMobile: React.FC<Props> = ({
  className,
  stats,
  tagline = '',
  coverDeviation,
  profileOwnerUser,
  isAuthorizedToCustomize,
  canChangeAvatar,
  isOwner,
  isGroup,
  hasSubscriptions,
  openAvatarEditor,
}) => {
  const newSubscribeSubnav = useFlag('subscribe_subnav');
  const userBirthday = useFlag('user_birthday');
  const isGroupProfile = useSelector(getIsGroupProfile);

  const showSubscribeButton =
    newSubscribeSubnav && hasSubscriptions && !isOwner;

  const showIconOnlyNotes =
    hasSubscriptions && !profileOwnerUser.isSubscribed && newSubscribeSubnav;
  const hideWatchButton = profileOwnerUser.isSubscribed && newSubscribeSubnav;

  const showStats = isGroupProfile
    ? ['founded', 'members', 'watchers']
    : ['watchers', 'deviations', 'pageviews'];

  const rootDivRef = useRef<HTMLDivElement>(null);
  const { hero } = useHeroImageForContainer(coverDeviation, rootDivRef, {
    isViewportWidth: true,
    width: 1024,
    height: 160,
    mobileWidth: 412,
    mobileHeight: 160,
  });
  const openEditor = useCallback(
    e => {
      canChangeAvatar && openAvatarEditor(true);
      e.preventDefault();
    },
    [canChangeAvatar, openAvatarEditor]
  );

  return (
    <div className={classnames(s['root'], className)}>
      <div className={s['hero-wrapper']} ref={rootDivRef}>
        {coverDeviation ? (
          <div
            className={classnames(s['background'], s['hero'])}
            style={{
              backgroundImage: hero ? `url(${hero.src})` : undefined,
            }}
          >
            {hero && (
              /* lets the preloader scan it */
              <img
                src={hero.src}
                alt=""
                width="0"
                height="0"
                style={{ display: 'none' }}
              />
            )}
          </div>
        ) : (
          <div className={classnames(s['background'], s['no-image'])}>
            <div className={s['no-image-meteors']} />
          </div>
        )}
        <MonetizationPromoBanner className={s['tier-banner']} />
        {!isGroup && !isOwner && (
          <MoreActionsDropdownMobile className={s['more-menu']} />
        )}
      </div>
      <div className={s['avatar']}>
        {userBirthday && profileOwnerUser.isBirthweek && (
          <AvatarBirthdayBadge />
        )}
        <div className={s['avatar-image']} onClick={openEditor}>
          <UserAvatar user={profileOwnerUser} size={100} />
        </div>
        {canChangeAvatar && <AvatarEdit />}
      </div>
      <div className={s['hug']}>
        <div className={s['info-text']}>
          <div className={s['username-skin-button-wrapper']}>
            <UserLink className={s['username']} user={profileOwnerUser} />
            {isAuthorizedToCustomize && (
              <SkinButton className={s['skin-button']} />
            )}
          </div>
          <div className={s['tagline']}>{tagline}</div>
        </div>
        {!isGroup && !isOwner && (
          <div className={s['action-bar']}>
            {
              <NotesButton
                user={profileOwnerUser}
                className={classnames(
                  newSubscribeSubnav && s['cta-button'],
                  showIconOnlyNotes && s['cta-button-icon-only']
                )}
                iconOnly={showIconOnlyNotes}
              />
            }
            {!hideWatchButton && <WatchButton user={profileOwnerUser} />}
            {showSubscribeButton && (
              <ProfileOwnerSubscribeButton
                user={profileOwnerUser}
                className={s['cta-button']}
              />
            )}
          </div>
        )}
        {isGroup && (
          <div className={s['group-action-bar']}>
            <JoinGroupButton
              type="solid"
              group={profileOwnerUser}
              className={s['cta-button']}
            />
            <MoreActionsDropdownMobile />
          </div>
        )}
        <div className={s['stats']}>
          {showStats.map(stat => (
            <ProfileStatItem
              key={stat}
              stat={stat}
              value={stats[stat]}
              className={s['stat-item']}
              countClassName={s['stat-item-count']}
            />
          ))}
        </div>
      </div>
    </div>
  );
};
ProfileHeroMobile.displayName = 'ProfileHeroMobile';

export default ProfileHeroMobile;
